/**
 * Plugin finds all svg files in an <img> tag and converts to svg inline
 */

class PluginSVGConverter {
  svgImages: string
  /**
   * @constructor
   */
  constructor(containerAttr: string) {
    this.svgImages = containerAttr

    // Find all svg images with given attr
    const svgImages = document.querySelectorAll(this.svgImages)

    // Check if any svg have been found
    if (svgImages.length > 0) {
      // Init all basic functionalities
      this.init(svgImages)
    } else {
      console.error(
        '#PluginSVGConverter ERROR! Missing elements to properly init svg converter',
      )
    }
  }

  /**
   * Init svg converter functionalities
   */
  init(svgImages: NodeListOf<Element>) {
    // If true, handle to every svg images
    svgImages.forEach((svgImage) => {
      // Check if handler for this svg image has been already added
      if (svgImage.getAttribute('data-svg-init') != 'true') {
        // Set attr that is not initialized
        svgImage.setAttribute('data-svg-init', 'true')

        // Init function to handle svg image
        this.handleSVGImage(svgImage as HTMLElement)
      }
    })
  }

  /**
   * Handle svg converter functionalities for single image
   * @param {HTMLElement} svgImage - single svgimage
   */
  handleSVGImage(svgImage: HTMLElement) {
    const img = svgImage
    const method = 'GET'

    // get image attributes
    const imgURL = img.getAttribute('src') as string

    this.loadData(method, imgURL, img)
      .then(function (res) {
        console.log('ok')
      })
      .catch(function (err) {
        console.error('#PluginSVGConverter ERROR!')
      })
  }

  loadData(method: string, imgURL: string, img: HTMLElement) {
    return new Promise((resolve, reject) => {
      fetch(imgURL)
        .then((response) => response.text())
        .then((data) => {
          this.convertData(img, data)
        })
    })
  }

  convertData(img: HTMLElement, data: string) {
    // get image attributes
    const imgID = img.getAttribute('id')
    const imgClass = img.getAttribute('class')
    const imgTitle = img.getAttribute('data-title')
    const imgDescription = img.getAttribute('data-description')
    const svgGradient = img.getAttribute('data-svg-gradient')
    const svgGradientStopColors = img.getAttribute(
      'data-svg-gradient-stop-colors',
    )
    const svgGradientStopOffsets = img.getAttribute(
      'data-svg-gradient-stop-offsets',
    )

    // Create a new dom parser to turn the SVG string into an element.
    const parser = new DOMParser()

    // Turn the raw text into a document with the svg element in it.
    const parsed = parser.parseFromString(data, 'text/html')

    // Select the <svg> element from that document.
    const svg = parsed.querySelector('svg') as SVGSVGElement

    if (svg) {
      // Store the SVG namespace for easy reuse.
      const xmlns = svg.getAttribute('xmlns')
      const xlink = svg.getAttribute('xmlns:xlink')
      const x = svg.getAttribute('x')
      const y = svg.getAttribute('y')
      const viewBox = svg.getAttribute('viewBox')
      const svgSstyle = svg.getAttribute('style')
      const xmlSpace = svg.getAttribute('xml:space')
      const newSvg = document.createElementNS(xmlns, 'svg')

      // Assign a attribute to the `<svg>` element so that it is visible.
      xmlns && newSvg.setAttribute('xmlns', xmlns)
      xlink && newSvg.setAttribute('xmlns:xlink', xlink)
      x && newSvg.setAttribute('x', x)
      y && newSvg.setAttribute('y', y)
      viewBox && newSvg.setAttribute('viewBox', viewBox)
      svgSstyle && newSvg.setAttribute('style', svgSstyle)
      xmlSpace && newSvg.setAttribute('xml:space', xmlSpace)

      // generate random number
      const number = Math.floor(Math.random() * 100)

      // if defined image id
      if (imgID) {
        newSvg.id = imgID
      }

      // if defined image class
      if (imgClass) {
        newSvg.classList.add('replaced-svg')
        newSvg.classList.add(imgClass)
      }

      // if defined image title
      if (imgTitle) {
        // Create title element
        const title = this.handleImgTitle(imgTitle, imgClass, number)

        // Add title in new svg
        newSvg.prepend(title)
      }

      // if defined image description
      if (imgDescription) {
        // Create description element
        const desc = this.handleImgDescription(imgDescription, imgClass, number)

        // Add desription in new svg
        newSvg.prepend(desc)
      }

      newSvg.removeAttribute('xmlns:a')

      newSvg.setAttribute('role', 'img')
      newSvg.setAttribute(
        'aria-labelledby',
        imgClass == null ? 'title' + number : imgClass + number,
      )

      if (
        !newSvg.hasAttribute('viewBox') &&
        newSvg.hasAttribute('height') &&
        newSvg.hasAttribute('width')
      ) {
        newSvg.setAttribute(
          'viewBox',
          '0 0 ' +
            newSvg.getAttribute('height') +
            ' ' +
            newSvg.getAttribute('width'),
        )
      }

      // Add gradient
      if (
        svgGradient &&
        svgGradientStopColors &&
        svgGradientStopOffsets &&
        xmlns
      ) {
        const defs = this.handleSVGGradient(
          svgGradient,
          svgGradientStopColors,
          svgGradientStopOffsets,
          xmlns,
        )

        // Set up the `<rect>` element.
        newSvg.setAttribute('fill', 'url(#Gradient)')

        // Add the `<defs>` and `svg body` elements to `<svg>`.
        newSvg.insertAdjacentElement('afterbegin', defs)
      }

      const body = svg.innerHTML

      // Remove tag style and attributes fill and class in body svg
      const regex = / fill="([^"]*)"|<style type="([^"]*)">([^"]*)<\/style>/gm
      const newBody = body.replace(regex, '')

      newSvg.insertAdjacentHTML('afterbegin', newBody)

      if (img && newSvg) {
        img.replaceWith(newSvg)
        img.style.opacity = '1'
      }
    }
  }

  handleImgTitle(imgTitle: string, imgClass: string | null, number: number) {
    const title = document.createElement('title')
    title.innerText = imgTitle
    title.setAttribute(
      'id',
      imgClass == null ? 'title' + number : imgClass + number,
    )

    return title
  }

  handleImgDescription(
    imgDescription: string,
    imgClass: string | null,
    number: number,
  ) {
    const desc = document.createElement('desc')
    desc.innerText = imgDescription
    desc.setAttribute(
      'id',
      imgClass == null ? 'desc' + number : imgClass + number,
    )

    return desc
  }

  handleSVGGradient(
    svgGradient: string,
    svgGradientStopColors: string,
    svgGradientStopOffsets: string,
    xmlns: string,
  ) {
    const defs = document.createElementNS(xmlns, 'defs')
    const gradient = document.createElementNS(xmlns, `${svgGradient}`)

    if (svgGradientStopColors) {
      const colors = svgGradientStopColors.split(',')

      colors.forEach((color, index) => {
        const el = document.createElementNS(xmlns, 'stop')
        el.setAttribute('stop-color', color)

        if (svgGradientStopOffsets) {
          const offsets = svgGradientStopOffsets.split(',')
          if (offsets[index]) {
            el.setAttribute('offset', offsets[index])
          }
        }

        // Add the `<stop>` element to `<linearGradient>`.
        gradient.appendChild(el)
      })
    }

    gradient.id = 'Gradient'
    gradient.setAttribute('x1', '0')
    gradient.setAttribute('x2', '1')
    gradient.setAttribute('y1', '0')
    gradient.setAttribute('y2', '1')
    defs.appendChild(gradient)

    return defs
  }
}

window.addEventListener('load', () => {
  new PluginSVGConverter(`[data-item="svg"]`)
})

// export default PluginSVGConverter;
